import React from 'react';
import { Briefcase, CheckCircle, Clock, Target, Users, ArrowRight, Plus } from 'lucide-react';
import { useState, useEffect } from 'react';
import ProjectForm from './ProjectForm';
import { useNavigate } from 'react-router-dom';
import { getProjects } from '../../lib/supabase/projects';
import { getTasks } from '../../lib/supabase/tasks';
import { useEntity } from '../../hooks/useEntity';
import { format } from 'date-fns';

export default function WorkshopOverview() {
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [stats, setStats] = useState({
    activeProjects: 0,
    openTasks: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    teamMembers: 0,
    upcomingDeadlines: 0
  });
  const [recentActivity, setRecentActivity] = useState<Array<{
    title: string;
    action: string;
    projectId?: string;
    taskId?: string;
    time: string;
    icon: any;
    color: string;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { entity } = useEntity();
  
  useEffect(() => {
    if (entity?.id) {
      loadData();
    }
  }, [entity?.id]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load projects
      const projects = await getProjects(entity!.id);
      
      // Load tasks
      const tasks = await getTasks('workshop', entity!.id, true);
      
      // Calculate stats
      const activeProjects = projects.filter(p => p.status !== 'completed' && p.status !== 'on_hold').length;
      const openTasks = tasks.length;
      const completedTasks = tasks.filter(t => t.status === 'completed').length;
      const inProgressTasks = tasks.filter(t => t.status === 'in_progress').length;
      
      // Calculate upcoming deadlines
      const now = new Date();
      const oneWeekFromNow = new Date();
      oneWeekFromNow.setDate(now.getDate() + 7);
      
      const upcomingDeadlines = tasks.filter(t => {
        if (!t.due_date) return false;
        const dueDate = new Date(t.due_date);
        return dueDate > now && dueDate <= oneWeekFromNow;
      }).length;
      
      // Update stats
      setStats({
        activeProjects,
        openTasks,
        completedTasks,
        inProgressTasks,
        teamMembers: projects.reduce((sum, p) => sum + (p.team_size || 0), 0),
        upcomingDeadlines
      });
      
      // Generate recent activity
      const recentActivity = [];
      
      // Add recent completed tasks
      const recentCompletedTasks = tasks
        .filter(t => t.status === 'completed')
        .sort((a, b) => new Date(b.updated_at || b.created_at || '').getTime() - new Date(a.updated_at || a.created_at || '').getTime())
        .slice(0, 2);
        
      for (const task of recentCompletedTasks) {
        const projectName = projects.find(p => p.id === task.project_id)?.name || 'Unknown Project';
        const updatedAt = new Date(task.updated_at || task.created_at || '');
        const timeAgo = getTimeAgo(updatedAt);
        
        recentActivity.push({
          title: projectName,
          action: `Task completed: ${task.title}`,
          projectId: task.project_id,
          taskId: task.id,
          time: timeAgo,
          icon: CheckCircle,
          color: "text-green-500"
        });
      }
      
      // Add recent projects
      const recentProjects = projects
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 2);
        
      for (const project of recentProjects) {
        const createdAt = new Date(project.created_at);
        const timeAgo = getTimeAgo(createdAt);
        
        recentActivity.push({
          title: project.name,
          action: project.status === 'planning' ? 'Project created' : `Project ${project.status.replace('_', ' ')}`,
          projectId: project.id,
          time: timeAgo,
          icon: Briefcase,
          color: "text-indigo-500"
        });
      }
      
      // Add recent in-progress tasks
      const recentInProgressTasks = tasks
        .filter(t => t.status === 'in_progress')
        .sort((a, b) => new Date(b.updated_at || b.created_at || '').getTime() - new Date(a.updated_at || a.created_at || '').getTime())
        .slice(0, 1);
        
      for (const task of recentInProgressTasks) {
        const projectName = projects.find(p => p.id === task.project_id)?.name || 'Unknown Project';
        const updatedAt = new Date(task.updated_at || task.created_at || '');
        const timeAgo = getTimeAgo(updatedAt);
        
        recentActivity.push({
          title: projectName,
          action: `Task in progress: ${task.title}`,
          projectId: task.project_id,
          taskId: task.id,
          time: timeAgo,
          icon: Target,
          color: "text-blue-500"
        });
      }
      
      // Sort by most recent
      recentActivity.sort((a, b) => {
        const timeA = parseTimeAgo(a.time) || Number.MAX_SAFE_INTEGER;
        const timeB = parseTimeAgo(b.time) || Number.MAX_SAFE_INTEGER;
        return timeA - timeB;
      });
      
      setRecentActivity(recentActivity.slice(0, 3));
      
    } catch (error) {
      console.error('Failed to load workshop data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to get time ago string
  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Unknown time';
    }
    
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return diffDays === 1 ? 'Yesterday' : `${diffDays} days ago`;
    }
    if (diffHours > 0) {
      return `${diffHours} hours ago`;
    }
    if (diffMins > 0) {
      return `${diffMins} minutes ago`;
    }
    return 'Just now';
  };
  
  // Helper function to parse time ago string for sorting
  const parseTimeAgo = (timeAgo: string): number => {
    if (timeAgo === 'Just now' || timeAgo === 'Unknown time') return 0;
    if (timeAgo === 'Yesterday') return 24 * 60;
    
    const match = timeAgo.match(/^(\d+)\s+(\w+)\s+ago$/);
    if (!match) return Number.MAX_SAFE_INTEGER;
    
    const [_, amount, unit] = match;
    const value = parseInt(amount);
    
    if (unit.startsWith('minute')) return value;
    if (unit.startsWith('hour')) return value * 60;
    if (unit.startsWith('day')) return value * 24 * 60;
    
    return null;
  };
  
  const handleCreateProject = () => {
    setShowProjectForm(true);
  };
  
  const handleProjectCreated = () => {
    setShowProjectForm(false);
    // Navigate to projects page to see the new project
    loadData(); // Refresh data after creating a project
    navigate('/dashboard', { state: { section: 'workshop', area: 'projects' } });
  };
  
  const handleAddTask = () => {
    // Navigate to tasks page with add task dialog open
    navigate('/dashboard', { 
      state: { 
        section: 'workshop', 
        area: 'tasks',
        showAddTask: true
      } 
    });
  };
  
  const handleViewProject = (projectId: string) => {
    navigate('/dashboard', { 
      state: { 
        section: 'workshop', 
        area: 'projects',
        projectId
      } 
    });
  };

  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-8 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Welcome to the Workshop</h1>
            <p className="text-blue-100">
              I'm Seisaku (制作), your Production Floor Manager. Let's turn your ideas into reality.
            </p>
          </div>
          <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm">
            <Briefcase className="w-8 h-8" />
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">Projects</h3>
            <Briefcase className="w-5 h-5 text-blue-500" />
          </div> 
          <p className="text-3xl font-bold text-gray-900">
            {loading ? (
              <span className="inline-block w-8 h-8 bg-gray-200 rounded-md animate-pulse"></span>
            ) : (
              stats.activeProjects
            )}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {loading ? (
              <span className="inline-block w-24 h-4 bg-gray-200 rounded-md animate-pulse"></span>
            ) : (
              `${stats.activeProjects === 0 ? 'No' : stats.activeProjects} active project${stats.activeProjects !== 1 ? 's' : ''}`
            )}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">Tasks</h3>
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {loading ? (
              <span className="inline-block w-8 h-8 bg-gray-200 rounded-md animate-pulse"></span>
            ) : (
              stats.openTasks
            )}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {loading ? (
              <span className="inline-block w-24 h-4 bg-gray-200 rounded-md animate-pulse"></span>
            ) : (
              `${stats.inProgressTasks} in progress`
            )}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">Team Members</h3>
            <Users className="w-5 h-5 text-indigo-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {loading ? (
              <span className="inline-block w-8 h-8 bg-gray-200 rounded-md animate-pulse"></span>
            ) : (
              stats.teamMembers
            )}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {loading ? (
              <span className="inline-block w-24 h-4 bg-gray-200 rounded-md animate-pulse"></span>
            ) : (
              `${stats.teamMembers === 0 ? 'No' : stats.teamMembers} team member${stats.teamMembers !== 1 ? 's' : ''}`
            )}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">Upcoming</h3>
            <Clock className="w-5 h-5 text-orange-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {loading ? (
              <span className="inline-block w-8 h-8 bg-gray-200 rounded-md animate-pulse"></span>
            ) : (
              stats.upcomingDeadlines
            )}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {loading ? (
              <span className="inline-block w-24 h-4 bg-gray-200 rounded-md animate-pulse"></span>
            ) : (
              `deadline${stats.upcomingDeadlines !== 1 ? 's' : ''} this week`
            )}
          </p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
          <button onClick={() => navigate('/dashboard', { state: { section: 'workshop', area: 'projects' } })} className="text-blue-500 hover:text-blue-600 text-sm flex items-center">
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </button>
        </div>

        <div className="space-y-4">
          {loading ? (
            // Loading skeleton
            Array(3).fill(0).map((_, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-5 h-5 bg-gray-200 rounded-full mt-1"></div>
                <div className="flex-1">
                  <div className="w-32 h-5 bg-gray-200 rounded-md mb-1"></div>
                  <div className="w-48 h-4 bg-gray-200 rounded-md mb-1"></div>
                  <div className="w-24 h-3 bg-gray-200 rounded-md"></div>
                </div>
              </div>
            ))
          ) : recentActivity.length > 0 ? (
            // Real activity data
            recentActivity.map((item, index) => (
              <div 
                key={index} 
                className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                onClick={() => item.projectId && handleViewProject(item.projectId)}
              >
                <div className={`${item.color} mt-1`}>
                  <item.icon className="w-5 h-5" />
                </div>
                
                
                <div>
                  <p className="text-gray-900 font-medium">{item.title}</p>
                  <p className="text-gray-500 text-sm">{item.action}</p>
                  <p className="text-gray-400 text-xs">{item.time}</p>
                </div>
              </div>
            ))
          ) : (
            // No activity
            <div className="text-center py-4 text-gray-500">
              <p>No recent activity</p>
              <p className="text-sm mt-1">Create your first project to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button 
              onClick={handleCreateProject}
              className="flex items-center justify-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>New Project</span>
            </button>
            <button 
              onClick={handleAddTask}
              className="flex items-center justify-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Task</span>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">AI Assistance</h3>
          <p className="text-gray-600 mb-4">Need help? I can assist with:</p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Project planning and organization</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Task breakdown and estimation</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Workflow optimization</span>
            </li>
          </ul>
        </div>
      </div>
      
      {/* Project Form Modal */}
      {showProjectForm && (
        <ProjectForm 
          onClose={() => setShowProjectForm(false)} 
          onSuccess={() => {
            handleProjectCreated();
            loadData(); // Refresh data after creating a project
          }}
        />
      )}
    </div>
  );
}